import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";

const FaqItem = ({ item, isOpen, onToggle, index }) => (
  <div
    className={`rounded-2xl border transition-all duration-300 ${
      isOpen
        ? "bg-white border-[#E8590A]/40 shadow-[0_16px_50px_-24px_rgba(232,89,10,0.28)]"
        : "bg-white border-[#EDEDED] hover:border-[#E8590A]/25"
    }`}
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full text-left px-5 sm:px-7 py-5 flex items-start justify-between gap-4 group"
      aria-expanded={isOpen}
    >
      <span className={`text-[15px] sm:text-base lg:text-[17px] font-semibold leading-snug pt-0.5 transition-colors ${isOpen ? "text-[#E8590A]" : "text-[#1B1B1F] group-hover:text-[#E8590A]"}`}>
        {item.q}
      </span>
      <span
        className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
          isOpen ? "bg-[#E8590A] text-white rotate-45" : "bg-[#F5F5F5] text-[#666] group-hover:bg-[#FFEDDD] group-hover:text-[#E8590A]"
        }`}
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div className="px-5 sm:px-7 pb-6 pt-0 text-[15px] leading-relaxed text-[#5A5A5A] max-w-[720px]">
            {item.a}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FaqSection = ({ items, title, id = "faq" }) => {
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    if (!items || items.length === 0) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-schema", "faq");
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => el.remove();
  }, [items]);

  if (!items || items.length === 0) return null;

  return (
    <section id={id} className="py-14 md:py-20 bg-[#FAFAFA]" data-testid="faq-section">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-10 lg:mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[52px] font-bold text-[#333] leading-[1.05] tracking-tight">
              Питання та <span className="text-[#E8590A]">відповіді</span>
            </h2>
            {title && <p className="text-base text-[#666] mt-3">{title}</p>}
          </div>
        </ScrollReveal>

        <div className="space-y-3">
          {items.map((it, i) => (
            <ScrollReveal key={i} delay={i * 0.04}>
              <FaqItem item={it} index={i} isOpen={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};
